import "./address-book-modal.css";

import { DialogActionsBar } from "@tql/ui-components-react/kendo/dialogs";
import type { GridPageChangeEvent } from "@tql/ui-components-react/kendo/grid";
import type { SwitchChangeEvent } from "@tql/ui-components-react/kendo/inputs";
import { Switch } from "@tql/ui-components-react/kendo/inputs";
import { Label } from "@tql/ui-components-react/kendo/labels";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { SaveLegacyConfirmationModal } from "~/features/address-book/components/save-legacy-confirmation-modal/save-legacy-confirmation-modal.tsx";
import { useItemsPerPagePreference } from "~/features/profile/hooks/use-items-per-page-preference.ts";
import { TraxGrid } from "~/shared/components/trax-grid/trax-grid.tsx";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import { useIsMobile } from "~/shared/hooks/use-is-mobile.ts";
import type { AddressBookItemSummary } from "~/shared/schemas/address-book-schema.ts";
import type { SubGroupKey } from "~/shared/types/quote-details-types.ts";
import { GridIdentifiers } from "~/shared/utils/grid-identifier-enums.ts";

import { getAddressBookColumnConfig } from "./components/address-book-grid-columns/address-book-grid-columns.tsx";
import { AddressBookRowDetail } from "./components/address-book-row-detail/address-book-row-detail.tsx";
import { MobileAddressEditModal } from "./components/mobile-address-edit-modal/mobile-address-edit-modal.tsx";
import { MobileAddressList } from "./components/mobile-address-list/mobile-address-list.tsx";
import type { AddressBookLocationFilter } from "./hooks/use-address-book-data.ts";
import { useAddressBookData } from "./hooks/use-address-book-data.ts";
import { useAddressDeletion } from "./hooks/use-address-deletion.ts";
import { useAddressEditing } from "./hooks/use-address-editing.ts";
import { useAddressLegacySaving } from "./hooks/use-address-legacy-saving.ts";
import { useAddressSelection } from "./hooks/use-address-selection.ts";
import type { AddressBookModalRow } from "./types.ts";

type AddressBookModalProps = {
	onClose: () => void;
	name: SubGroupKey;
	/** Customer whose address book is browsed. */
	customerId: number;
	/** When true, filters address book items by the provided `locationFilter`. */
	useLocationFilter?: boolean;
	/** Location used to filter the address book when `useLocationFilter` is set. */
	locationFilter?: AddressBookLocationFilter;
	/**
	 * Called with the selected address when the user picks one. Callers own any
	 * side effects (e.g. applying the address to their own state).
	 */
	onSelect?: (address: AddressBookItemSummary) => void;
	/**
	 * When provided, renders an "Add New Address" action in the modal footer that
	 * invokes this callback (e.g. to open the create-address form). Kept optional
	 * so only callers that opt in (currently shipment management) show it.
	 */
	onAddNew?: () => void;
};

export const AddressBookModal = ({
	onClose,
	name,
	customerId,
	useLocationFilter = false,
	locationFilter,
	onSelect,
	onAddNew,
}: AddressBookModalProps) => {
	const isMobile = useIsMobile();
	const [legacyEnabled, setLegacyEnabled] = useState(true);

	// Pagination state management with items-per-page preference persistence
	const {
		itemsPerPage,
		updateItemsPerPage,
		isLoading: isLoadingPreference,
	} = useItemsPerPagePreference(GridIdentifiers["Address Book Modal"]);
	const [skip, setSkip] = useState<number>(0);
	const [take, setTake] = useState<number>(itemsPerPage);

	// Once the user manually changes the page size, the local `take` becomes the
	// source of truth. Without this guard, the async preference round-trip (or a
	// stale PUT response) would re-emit the previous itemsPerPage and overwrite
	// the user's selection.
	const hasUserSetTake = useRef(false);

	// Sync take with itemsPerPage when the preference first loads (before any manual interaction).
	useEffect(() => {
		if (!hasUserSetTake.current) {
			setTake(itemsPerPage);
		}
	}, [itemsPerPage]);

	const onSetSelectedItem = useCallback(
		(address: AddressBookItemSummary) => {
			onSelect?.(address);
			onClose();
		},
		[onSelect, onClose],
	);

	const { data, setData, isLoading, setIsLoading, filteredQueryParams } =
		useAddressBookData({
			showModal: true,
			customerId,
			useLocationFilter,
			addressType: name,
			locationFilter,
		});

	// Combined loading state for pagination operations
	const isLoadingForPagination = isLoadingPreference || isLoading;

	// Filter data based on stop type: pickup (stopType === 1) or delivery (stopType === 2)
	const filteredData = useMemo(() => {
		const targetStopType = name === "pickup" ? 1 : 2;

		// Get IDs of legacy addresses that have been converted to new addresses
		const convertedLegacyIds = new Set(
			data
				.filter((item) => !item.isLegacy && item.legacyAddressId)
				.map((item) => item.legacyAddressId),
		);

		return data.filter(
			(item) =>
				// Hide legacy items that have already been converted
				!(item.isLegacy && convertedLegacyIds.has(item.addressBookItemId)) &&
				(item.isLegacy ||
					item.details.some((detail) => detail.stopType === targetStopType)),
		);
	}, [data, name]);

	// Transform data to make hours searchable - include full time range in hoursOpen for filtering
	const dataWithSearchableHours: AddressBookModalRow[] = useMemo(() => {
		const targetStopType = name === "pickup" ? 1 : 2;
		const legacyFiltered = legacyEnabled
			? filteredData
			: filteredData.filter((r) => !r.isLegacy);
		return legacyFiltered.map((item) => {
			let detail = item.details.find((d) => d.stopType === targetStopType);

			// Fallback for legacy addresses which might have a different stopType representation or just one detail
			if (!detail && item.isLegacy && item.details.length > 0) {
				detail = item.details[0];
			}

			return {
				...item,
				// Add id field for SelectCell compatibility (v2 API uses addressBookItemId)
				id: item.addressBookItemId,
				// Flatten contact fields from details for grid editing
				contactName: detail?.contactName ?? "",
				contactPhone: detail?.contactPhone ?? "",
				contactExtension: detail?.contactExtension ?? "",
				cityStateZip: `${item.city || ""}, ${item.stateProvince || ""} ${
					item.postalCode || ""
				}`,
				// Store original values for display
				originalHoursOpen: detail?.hoursOpen ?? "",
				originalHoursClose: detail?.hoursClose ?? "",
				// Override hoursOpen with searchable content for filtering
				hoursOpen: `${detail?.hoursOpen || ""} - ${detail?.hoursClose || ""}`,
			};
		});
	}, [filteredData, legacyEnabled, name]);

	const { selectedItems, select, handleSelect, handleDeselect } =
		useAddressSelection({
			data: dataWithSearchableHours,
			showModal: true,
		});

	const {
		edit,
		isEditMode,
		detailExpand,
		handleExpandChange,
		handleEditChange,
		enterEdit,
		handleSaveEdit,
		handleCancelEdit,
		itemChange,
		applyAddressFields,
	} = useAddressEditing({
		data: dataWithSearchableHours,
		setData,
		setIsLoading,
		showModal: true,
		stopType: name === "pickup" ? 1 : 2,
	});
	const {
		saveLegacyConfirmation,
		closeSaveLegacyConfirmation,
		handleConfirmSaveLegacy,
	} = useAddressLegacySaving();

	const {
		showConfirmation,
		itemToDelete,
		handleRemoveFromList,
		handleConfirmDelete,
		handleCancelDelete,
	} = useAddressDeletion({
		setData,
		setIsLoading,
		showModal: true,
		stopType: name === "pickup" ? 1 : 2,
	});

	// On mobile, Edit opens a dedicated modal instead of the grid's inline cell
	// editors + expanded row detail used on desktop.
	const editingItem = useMemo(
		() => dataWithSearchableHours.find((item) => edit[item.addressBookItemId]),
		[dataWithSearchableHours, edit],
	);

	const handleAddSelectedAndClose = useCallback(() => {
		if (selectedItems.length === 0 || !selectedItems[0]) return;
		onSetSelectedItem(selectedItems[0]);
	}, [selectedItems, onSetSelectedItem]);

	const handleAddAndClose = useCallback(
		(dataItem: AddressBookItemSummary) => {
			onSetSelectedItem(dataItem);
		},
		[onSetSelectedItem],
	);

	// `handleCancelEdit`/`handleSaveEdit` are intentionally NOT passed here: they
	// are only used by the row-detail (below) and their identity changes on
	// interaction. Including them would regenerate this memo, handing Kendo new
	// cell renderers and remounting every cell — which drops focus from an
	// in-cell editor (e.g. the City/State/Zip postal-code search) mid-edit.
	const columnConfig = useMemo(
		() =>
			getAddressBookColumnConfig({
				type: name,
				isEditMode,
				isLoading,
				edit,
				select,
				handleSelect,
				handleDeselect,
				handleRemoveFromList,
				handleAddAndClose,
				enterEdit,
				applyAddressFields,
			}),
		[
			name,
			isEditMode,
			isLoading,
			edit,
			select,
			handleSelect,
			handleDeselect,
			handleRemoveFromList,
			handleAddAndClose,
			enterEdit,
			applyAddressFields,
		],
	);

	// Handle page change (pagination)
	// Prevent page changes while either preference or data is loading to ensure we don't
	// overwrite the user's saved preference or lose data integrity.
	const handlePageChange = useCallback(
		(event: GridPageChangeEvent) => {
			if (isLoadingForPagination) return;

			setSkip(event.page.skip);
			// Convert string to number if needed (Kendo may pass string values)
			let nextTake = 25;
			if (typeof event.page.take === "number") {
				nextTake = event.page.take;
			} else if (typeof event.page.take === "string") {
				const parsed = Number(event.page.take);
				nextTake = Number.isNaN(parsed) ? 25 : parsed;
			}
			// Only persist when the page SIZE changed. Next/Previous navigation
			// keeps the same `take`, so writing here would schedule a redundant
			// full-preference PUT.
			if (nextTake === take) return;
			hasUserSetTake.current = true;
			setTake(nextTake);
			updateItemsPerPage(nextTake);
		},
		[updateItemsPerPage, isLoadingForPagination, take],
	);

	const renderDetailRow = useCallback(
		(props: { dataItem: AddressBookItemSummary; dataIndex: number }) => {
			return (
				<AddressBookRowDetail
					{...props}
					edit={edit}
					itemChange={itemChange}
					name={name}
					onCancelEdit={handleCancelEdit}
					onEdit={enterEdit}
					onSave={handleSaveEdit}
				/>
			);
		},
		[edit, enterEdit, handleCancelEdit, handleSaveEdit, itemChange, name],
	);

	return (
		<>
			<TraxModal onClose={onClose} title={"Address Book"} width={"75%"}>
				{useLocationFilter && filteredQueryParams?.postalCode ? (
					<div className={"address-book-filter-message"}>
						Filtered by Postal Code: {filteredQueryParams.postalCode}
					</div>
				) : null}
				{isMobile ? (
					<MobileAddressList
						data={dataWithSearchableHours}
						handleDeselect={handleDeselect}
						handleRemoveFromList={handleRemoveFromList}
						handleSelect={handleSelect}
						isLoading={isLoadingForPagination}
						onEdit={enterEdit}
						select={select}
						type={name}
					/>
				) : (
					<TraxGrid
						className={"saved-commodities-grid"}
						columns={columnConfig}
						data={dataWithSearchableHours}
						detail={renderDetailRow}
						detailExpand={detailExpand}
						edit={edit}
						filterable={true}
						loading={isLoadingForPagination}
						loadingRows={5}
						maxHeight={"300px"}
						onDetailExpandChange={handleExpandChange}
						onEditChange={handleEditChange}
						onItemChange={itemChange}
						onPageChange={handlePageChange}
						skip={skip}
						take={take}
					/>
				)}

				<div className={"legacy-switch-wrapper"}>
					<Label editorId={"legacy-switch"}>Legacy</Label>
					<Switch
						checked={legacyEnabled}
						id={"legacy-switch"}
						onChange={(e: SwitchChangeEvent) => {
							setLegacyEnabled(e.value);
						}}
						size={"large"}
					/>
				</div>

				<DialogActionsBar>
					<div
						className={`saved-commodities-action-bar${
							isMobile ? " address-book-action-bar-mobile" : ""
						}`}
						data-testid={"address-book-action-bar"}
					>
						<Button
							disabled={selectedItems.length === 0}
							onClick={handleAddSelectedAndClose}
							variant={"primary"}
						>
							Add & Close
						</Button>
						{onAddNew ? (
							<Button onClick={onAddNew}>Add New Address</Button>
						) : null}
						<Button onClick={onClose}>Cancel</Button>
					</div>
				</DialogActionsBar>
			</TraxModal>
			{showConfirmation ? (
				<TraxModal
					cancelText={"Cancel"}
					confirmText={"Yes, Delete"}
					confirmVariant={"primary"}
					onCancel={handleCancelDelete}
					onClose={handleCancelDelete}
					onConfirm={handleConfirmDelete}
					title={"Confirm Deletion"}
					type={"confirmation"}
				>
					Are you sure you want to remove &quot;{itemToDelete?.companyName}
					&quot; from your address book?
				</TraxModal>
			) : null}
			{saveLegacyConfirmation.isOpen && saveLegacyConfirmation.dataItem ? (
				<SaveLegacyConfirmationModal
					companyName={saveLegacyConfirmation.dataItem.companyName ?? ""}
					onClose={closeSaveLegacyConfirmation}
					onConfirm={() => {
						void handleConfirmSaveLegacy();
					}}
				/>
			) : null}
			{isMobile && editingItem ? (
				<MobileAddressEditModal
					applyAddressFields={applyAddressFields}
					dataItem={editingItem}
					itemChange={itemChange}
					name={name}
					onCancel={handleCancelEdit}
					onSave={handleSaveEdit}
				/>
			) : null}
		</>
	);
};
